import { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ApiError } from 'api/core/ApiError';
import type { CompanyMeasurementDateModel } from 'api/models/CompanyMeasurementDateModel';
import type { EnsureFulfillmentForAttioValuationRequest } from 'api/models/EnsureFulfillmentForAttioValuationRequest';
import { ApiService } from 'api/services/ApiService';
import { DROPBOX_FOLDER_SYNC_QUERY_KEYS } from 'dashboard409a/components/OperationsModal/useDropboxFolderSync.constants';
import type { AttioValuationDetailResponse } from 'dashboard409a/services/shared-types';
import { dbShortDate } from 'utilities';
import type { FulfillmentSideEffectsResult } from './fulfillmentOnStatusChange';

export type ResolveCompanyIdForFolderSyncResult =
  | { status: 'ok'; companyId: number }
  | { status: 'loading' }
  | { status: 'failed'; reason: 'missing_valuation_date' | 'link_failed' };

const companyMeasurementDateQueryKey = (valuationId: string) =>
  [DROPBOX_FOLDER_SYNC_QUERY_KEYS.COMPANY_MEASUREMENT_DATE_BY_ATTIO_VALUATION_ID, valuationId] as const;

const readCompanyMeasurementDateByAttioValuationId = async (
  valuationId: string
): Promise<CompanyMeasurementDateModel | null> => {
  try {
    return await ApiService.apiCompanyMeasurementDatesRead(valuationId);
  } catch (error) {
    const isNotFound =
      (error instanceof ApiError && error.status === 404) ||
      (isAxiosError(error) && error.response?.status === 404);

    if (isNotFound) {
      return null;
    }
    throw error;
  }
};

const companyIdFromCmd = (cmd: CompanyMeasurementDateModel | null | undefined): number | undefined => {
  const id = cmd?.company?.id;
  return typeof id === 'number' ? id : undefined;
};

/** Refetch CMD by attio valuation id, optionally ensuring company/CMD exist as a fallback. */
export async function resolveCompanyIdForFolderSync(params: {
  queryClient: QueryClient;
  valuationId: string;
  knownCompanyId?: number;
  isCmdQueryLoading: boolean;
  valuationDetail?: AttioValuationDetailResponse;
  modalData: unknown;
}): Promise<ResolveCompanyIdForFolderSyncResult> {
  const { queryClient, valuationId, knownCompanyId, isCmdQueryLoading, valuationDetail, modalData } = params;

  if (knownCompanyId) {
    return { status: 'ok', companyId: knownCompanyId };
  }

  if (isCmdQueryLoading) {
    return { status: 'loading' };
  }

  const queryKey = companyMeasurementDateQueryKey(valuationId);

  await queryClient.invalidateQueries({ queryKey });
  await queryClient.refetchQueries({ queryKey });

  let cmd = queryClient.getQueryData<CompanyMeasurementDateModel | null>(queryKey);
  let companyId = companyIdFromCmd(cmd);
  if (companyId) {
    return { status: 'ok', companyId };
  }

  if (!valuationDetail) {
    cmd = await readCompanyMeasurementDateByAttioValuationId(valuationId);
    queryClient.setQueryData(queryKey, cmd);
    companyId = companyIdFromCmd(cmd);
    if (companyId) {
      return { status: 'ok', companyId };
    }
    return { status: 'failed', reason: 'link_failed' };
  }

  const ensureBase = buildEnsureFulfillmentBaseRequest({
    valuationDetail,
    valuationId,
    modalData,
  });
  if (!ensureBase) {
    return { status: 'failed', reason: 'missing_valuation_date' };
  }

  await ApiService.apiDashboardOperationsEnsureFulfillmentForAttioValuationCreate(ensureBase);
  await queryClient.invalidateQueries({ queryKey });
  await queryClient.refetchQueries({ queryKey });

  cmd = queryClient.getQueryData<CompanyMeasurementDateModel | null>(queryKey);
  companyId = companyIdFromCmd(cmd);
  if (companyId) {
    return { status: 'ok', companyId };
  }

  cmd = await readCompanyMeasurementDateByAttioValuationId(valuationId);
  queryClient.setQueryData(queryKey, cmd);
  companyId = companyIdFromCmd(cmd);
  if (companyId) {
    return { status: 'ok', companyId };
  }

  return { status: 'failed', reason: 'link_failed' };
}

/** Measurement date for CMD: valuation date from valued information (not deal date). */
export function parseValuationDateToIso(valuationDate: string | undefined | null): string | null {
  if (!valuationDate || valuationDate === '-') {
    return null;
  }
  return dbShortDate(valuationDate.trim());
}

function resolveAttioCompanyId(
  valuationDetail: AttioValuationDetailResponse,
  valuationId: string,
  modalData: unknown
): string | undefined {
  if (modalData && typeof modalData === 'object' && 'attio_company_id' in modalData) {
    const raw = (modalData as { attio_company_id?: string | null }).attio_company_id;
    if (raw) {
      return String(raw);
    }
  }
  const veId = valuationDetail.valuedEntity?.valuedEntityId;
  if (veId && veId !== '-' && veId !== valuationId) {
    return veId;
  }
  return undefined;
}

/** Base body for POST .../ensure-fulfillment-for-attio-valuation/ */
export function buildEnsureFulfillmentBaseRequest(params: {
  valuationDetail: AttioValuationDetailResponse;
  valuationId: string;
  modalData: unknown;
}): EnsureFulfillmentForAttioValuationRequest | null {
  const { valuationDetail, valuationId, modalData } = params;

  const measurementDateIso = parseValuationDateToIso(valuationDetail.valuedInformation?.valuationDate);
  if (!measurementDateIso) {
    return null;
  }

  const name =
    valuationDetail.valuedEntity?.companyName?.trim() ||
    valuationDetail.valuedInformation?.valuationName?.trim() ||
    'Company';

  const attioCompanyIdResolved = resolveAttioCompanyId(valuationDetail, valuationId, modalData);

  const payload: EnsureFulfillmentForAttioValuationRequest = {
    attio_valuation_id: valuationId,
    name,
    measurement_date: measurementDateIso,
  };

  if (attioCompanyIdResolved) {
    payload.attio_company_id = attioCompanyIdResolved;
  }

  return payload;
}

export function mergeFulfillmentSideEffectsIntoEnsureRequest(
  base: EnsureFulfillmentForAttioValuationRequest,
  side: FulfillmentSideEffectsResult
): EnsureFulfillmentForAttioValuationRequest {
  if (side.action === 'create' && side.createPayload) {
    const c = side.createPayload;
    return {
      ...base,
      model_status: c.model_status,
      supporting_due_date: c.supporting_due_date,
      ...(c.is_slc_model !== undefined && { is_slc_model: c.is_slc_model }),
      reviewers: c.reviewers,
      preparers: c.preparers,
    };
  }

  if (side.action === 'update' && side.updatePayload) {
    const u = side.updatePayload;
    return {
      ...base,
      ...(u.model_status !== undefined && { model_status: u.model_status }),
      ...(u.supporting_due_date !== undefined && { supporting_due_date: u.supporting_due_date }),
      ...(u.is_slc_model !== undefined && { is_slc_model: u.is_slc_model }),
      ...(u.is_waiting !== undefined && { is_waiting: u.is_waiting }),
    };
  }

  return { ...base };
}
